import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import bancameLogo from '../../assets/bancame_logo_white.svg';

export interface HeroCardProps {
  heroImage: string;
  overlayGradient?: string;
  logo?: React.ReactNode;
  title?: React.ReactNode;
  body?: React.ReactNode;
  buttonText?: string;
  loading?: boolean;
  error?: boolean;
  errorMessage?: string;
  onConfirm?: () => void;
  button?: React.ReactElement | null;
  /**
   * `'default'` — botón debajo del body (comportamiento estándar).
   * `'inline'`  — botón alineado a la derecha del body en la misma fila.
   */
  variant?: 'default' | 'inline';
}

const DEFAULT_GRADIENT = 'linear-gradient(100deg, rgba(51,38,117,.93) 0%, rgba(51,38,117,.82) 55%, rgba(117,93,223,.55) 100%)';

function defaultLogo() {
  return (
    <Box
      component="img"
      src={bancameLogo}
      alt="banca.me"
      sx={{
        height: 15, width: 'auto', display: 'block', mb: 2.5,
      }}
    />
  );
}

export default function HeroCard({
  heroImage,
  overlayGradient = DEFAULT_GRADIENT,
  logo = defaultLogo(),
  title,
  body,
  buttonText,
  loading = false,
  error = false,
  errorMessage = 'Ocurrió un error. Por favor intenta nuevamente.',
  onConfirm,
  button,
  variant = 'default',
}: HeroCardProps) {
  const theme = useTheme();

  const ctaButton = (onConfirm && button)
    ? React.cloneElement(button, {
      onClick: onConfirm,
      disabled: loading,
      endIcon: !loading && <ArrowForwardIcon />,
    })
    : (onConfirm || buttonText) && (
      <Button
        variant="contained"
        onClick={onConfirm}
        disabled={loading}
        endIcon={!loading && <ArrowForwardIcon />}
        sx={{
          flexShrink: 0,
          alignSelf: variant === 'inline' ? 'center' : 'flex-start',
          bgcolor: theme.palette.white.main,
          color: theme.palette.primary.main,
          fontWeight: 700,
          borderRadius: 5,
          minWidth: 140,
          textTransform: 'none',
          width: 'auto',
          boxShadow: 'none',
          '&:hover': { bgcolor: '#E5E1F4', boxShadow: 'none' },
          '&:disabled': { bgcolor: alpha(theme.palette.white.main, 0.6) },
        }}
      >
        {loading
          ? <CircularProgress size={18} sx={{ color: theme.palette.primary.main }} />
          : buttonText}
      </Button>
    );

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '24px',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(51,38,117,.18)',
        minHeight: variant === 'inline' ? 0 : 200,
        display: 'flex',
        alignItems: 'stretch',
      }}
    >
      <Box
        component="img"
        src={heroImage}
        alt=""
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center 33%',
        }}
      />
      <Box sx={{ position: 'absolute', inset: 0, background: overlayGradient }} />
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          p: { xs: '24px 20px', sm: '28px 28px' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '100%',
          gap: 2,
        }}
      >
        <Box>
          {logo}
          {title}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: variant === 'inline' ? 'row' : 'column',
            alignItems: variant === 'inline' ? 'center' : 'flex-start',
            justifyContent: variant === 'inline' ? 'space-between' : 'flex-start',
            gap: 2,
          }}
        >
          <Box sx={{ flex: variant === 'inline' ? 1 : 'unset' }}>
            {error && (
              <Alert
                severity="error"
                sx={{
                  borderRadius: '12px', fontSize: 13, py: 0.5, mb: 1,
                }}
              >
                {errorMessage}
              </Alert>
            )}
            {body}
          </Box>

          {ctaButton}
        </Box>
      </Box>
    </Box>
  );
}

HeroCard.defaultProps = {
  overlayGradient: DEFAULT_GRADIENT,
  logo: undefined,
  title: undefined,
  body: undefined,
  buttonText: undefined,
  loading: false,
  error: false,
  errorMessage: 'Ocurrió un error. Por favor intenta nuevamente.',
  onConfirm: undefined,
  button: undefined,
  variant: 'default',
};
